/* eslint-disable valid-typeof */
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { WxButton, WxPrice, WxText, WxTitle } from '../..';
import useBankroll, {
  useHasEnough,
} from '../../../hooks/api/queries/useBankroll';
import { ProductType } from '../../../hooks/api/queries/useProduct';
import { ImageType } from '../../../hooks/api/queries/useProducts';
import { VariantType } from '../../../hooks/api/queries/useVariantsByProductId';
import {
  VariantCartItem,
  useCartContext,
} from '../../../hooks/context/useCartContext';
import useJwt from '../../../hooks/utils/useJwt';
import { __ } from '../../../i18n/l10n';
import { breakpoint } from '../../../styles/constants';
import { FavoriteIcon } from '../../ui/favorites/FavoriteIcon';
import { StyledFontAwesomeIcon } from '../cart/CartItem';
import { GiftModal } from './GiftModal';
import { productSpecsLabels } from './ProductSpecs';
import { VariantPicker } from './VariantPicker';
import { WxBonusModal } from './WxBonusModal';

interface Props {
  product: ProductType;
  images: ImageType[];
  variants: VariantType[];
  setImageSelected: (index: number) => void;
  imageSelected: number;
}

export const ProductInfos = ({
  product,
  images,
  variants,
  setImageSelected,
}: Props) => {
  const [addingProductToCart, setAddingProductToCart] =
    useState<boolean>(false);
  useEffect(() => {
    if (addingProductToCart === true)
      setTimeout(() => setAddingProductToCart(false), 2000);
  }, [addingProductToCart]);

  const [giftModal, setGiftModal] = useState<boolean>(false);
  const [bonusModal, setBonusModal] = useState<boolean>(false);
  const { addOne, cart } = useCartContext();
  const jwt = useJwt();
  const [quantity, setQuantity] = useState<number>(1);
  useBankroll();
  const hasEnough = useHasEnough();
  const price = product.priceOverwrite
    ? product.priceOverwrite.milesAmount
    : product.price.milesAmount;

  const [variantSelected, setVariantSelected] = useState<number>(
    variants.length === 1 && variants[0].isDefault ? 0 : -1,
  );

  const variantCartItem: VariantCartItem = {
    name: product.name,
    slug: product.slug,
    dematerialized: product.dematerialized,
    variantId: variants?.[variantSelected]?.variantId,
    price: product.price ?? 0,
    priceOverwrite: product.priceOverwrite ?? 0,
    quantity,
  };

  useEffect(() => {
    if (
      variants[variantSelected]?.localizedImage?.path ||
      (variants[variantSelected]?.defaultImage?.path && variants.length > 1)
    )
      setImageSelected(
        images.findIndex(
          (e) =>
            e.id ===
            (variants[variantSelected]?.localizedImage?.id ??
              variants[variantSelected]?.defaultImage?.id),
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantSelected]);

  const isForced =
    product.forceDisplay === 1 &&
    variants.filter((e) => e.quantity !== 0).length === 0;

  const isWxBonus =
    product.dematerializedItemType === 'BONUS' ||
    product.dematerializedItemType === 'TICKET' ||
    product.dematerializedItemType === 'CASH' ||
    product.dematerializedItemType === 'FREEBET' ||
    product.dematerializedItemType === 'JDE';

  return (
    <Wrapper>
      {product.gift && (
        <GiftModal
          open={giftModal}
          setOpen={setGiftModal}
          variant={variants?.[variantSelected]}
          product={product}
        />
      )}
      {isWxBonus && (
        <WxBonusModal
          open={bonusModal}
          setOpen={setBonusModal}
          variant={variants?.[variantSelected]}
          product={product}
        />
      )}
      <Flex>
        <WxTitle title={product.name} align={'left'} size={'medium'} darkMode />

        <WxPrice
          price={price}
          priceBefore={
            product.priceOverwrite?.milesAmount &&
            product.price &&
            product.price.milesAmount
          }
          dark
          noPadding
          style={{ paddingTop: 4 }}
        />
      </Flex>

      {variants.length > 0 && !variants[0].isDefault && !isForced ? (
        <VariantPicker
          variants={variants.filter((e) => !e.isDefault)}
          setVariantSelected={setVariantSelected}
          setImageSelected={setImageSelected}
          images={images}
        />
      ) : null}
      {isForced && product.nextProductAvailability && (
        <Availability>
          <WxText
            title={productSpecsLabels.nextProductAvailability}
            text={product.nextProductAvailability}
            darkMode
          />
        </Availability>
      )}
      <MaxWidth>
        <>
          {!isForced && !isWxBonus && (
            <QtyFlex>
              <StyledFontAwesomeIcon
                icon={faMinus}
                onClick={() => setQuantity((qty) => qty - 1)}
                disabled={quantity === 1 || variantSelected === -1}
              />
              {quantity}
              <StyledFontAwesomeIcon
                icon={faPlus}
                onClick={() => setQuantity((qty) => qty + 1)}
                disabled={
                  !jwt ||
                  variantSelected === -1 ||
                  quantity >=
                    variants[variantSelected].quantity -
                      (cart.items.find(
                        (item) =>
                          item.variantId ===
                          variants[variantSelected].variantId,
                      )?.quantity ?? 0)
                }
              />
            </QtyFlex>
          )}

          {jwt && (
            <CartMessages>
              {variants.length === 0 ? (
                <WxText type={'alert'} title={__('Out of stock')} darkMode />
              ) : variantSelected === -1 ? (
                <WxText
                  type={'alert'}
                  title={__('Please select colors and/or size')}
                  darkMode
                />
              ) : null}
            </CartMessages>
          )}

          <FlexColumn>
            <Flex style={{ marginTop: '0.625rem' }}>
              <FavoriteIcon productId={product.id} big />
              {jwt && !isForced ? (
                <WxButton
                  label={
                    addingProductToCart
                      ? __('Product added to cart')
                      : isWxBonus
                      ? __('Express order')
                      : __('Add to cart')
                  }
                  onClick={() => {
                    if (!addingProductToCart)
                      if (isWxBonus) {
                        if (
                          variantSelected !== -1 &&
                          hasEnough(variantCartItem) &&
                          quantity <=
                            variants[variantSelected].quantity -
                              (cart.items.find(
                                (item) =>
                                  item.variantId ===
                                  variants[variantSelected].variantId,
                              )?.quantity ?? 0)
                        )
                          setBonusModal(true);
                      } else {
                        if (
                          variantSelected !== -1 &&
                          quantity <=
                            variants[variantSelected].quantity -
                              (cart.items.find(
                                (item) =>
                                  item.variantId ===
                                  variants[variantSelected].variantId,
                              )?.quantity ?? 0)
                        ) {
                          variantSelected !== -1 && addOne(variantCartItem);
                          setQuantity(1);
                          setAddingProductToCart(true);
                        }
                      }
                  }}
                  darkMode={addingProductToCart}
                  noArrow
                  type={addingProductToCart ? 'bordered' : 'validation'}
                  disabled={
                    variantSelected === -1 ||
                    (isWxBonus && !hasEnough(variantCartItem))
                  }
                  expand
                />
              ) : !jwt && !isForced ? (
                <WxButton
                  label={__('Log in to order')}
                  onClick={() => {
                    if (typeof window !== undefined) window.showLoginBox();
                  }}
                  type={'alert'}
                  expand
                />
              ) : (
                <WxButton
                  label={__('Product unavailable')}
                  onClick={() => {}}
                  type={'disabled'}
                  disabled
                  expand
                />
              )}
            </Flex>
            <Flex>
              <HiddenNoClick>
                <FavoriteIcon productId={product.id} big />
              </HiddenNoClick>
              {jwt && product.gift && (
                <GiftBlock>
                  <WxButton
                    label={__('Offer')}
                    onClick={() => {
                      if (
                        variantSelected !== -1 &&
                        hasEnough(variantCartItem) &&
                        quantity <=
                          variants[variantSelected].quantity -
                            (cart.items.find(
                              (item) =>
                                item.variantId ===
                                variants[variantSelected].variantId,
                            )?.quantity ?? 0)
                      )
                        setGiftModal(true);
                    }}
                    type={'validation'}
                    disabled={
                      variantSelected === -1 || !hasEnough(variantCartItem)
                    }
                    expand
                  />
                  <GiftMessages>
                    {!hasEnough(variantCartItem) && (
                      <WxText
                        type={'alert'}
                        align={'center'}
                        text={__(
                          'Not enough miles available for immediate order',
                        )}
                        darkMode
                      />
                    )}
                  </GiftMessages>
                </GiftBlock>
              )}
            </Flex>
          </FlexColumn>
        </>
      </MaxWidth>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (min-width: ${breakpoint}) {
    width: 45%;
    gap: 0rem;
    > *:first-child {
      margin-bottom: 1.25rem;
    }
    /* flex: 1; */
  }
`;

const CartMessages = styled.div`
  margin-top: 1.25rem;
  margin-bottom: -0.75rem;
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const GiftBlock = styled.div`
  width: 100%;
`;
const GiftMessages = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 0.75rem;
  width: 100%;
  height: 50px;
  background-color: 'red';
`;

const Flex = styled.div`
  display: flex;
  width: 100%;
  gap: 0.625rem;
  justify-content: space-between;
  align-items: flex-start;

  @media (min-width: ${breakpoint}) {
    align-items: flex-start;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.625rem;
  justify-content: space-between;
  align-items: flex-end;

  @media (min-width: ${breakpoint}) {
    align-items: flex-end;
  }
`;

const HiddenNoClick = styled.div`
  visibility: hidden;
  pointer-events: none;
`;

const MaxWidth = styled.div`
  > :last-child {
    margin-top: 0.625rem;
  }

  @media (min-width: ${breakpoint}) {
    /* margin-top: auto; */
    max-width: 360px;

    > *:last-child {
      margin-bottom: 1.25rem;
    }
  }
`;

const QtyFlex = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.white};
  padding-inline: 0.9575rem;
  padding-block: 0.625rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.white};
`;

const Availability = styled.div`
  font-size: 12px;
  color: white;
`;
